import { apiOffers } from "@/api/recruit/offer.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { employeeTodayKeys } from "@/queries/employees/use-employee-today.query.ts";
import { candidateActionsKeys } from "@/queries/recruit/use-candidate-actions.query.ts";
import { ApiErrorException, ApiPassException } from "@/types/api.types.ts";
import {
  ApiOfferApproveRequest,
  ApiOfferConfirmationCommentUpdate,
  ApiOfferConfig,
  ApiOfferSavePayload,
  ApiOffersFilter,
} from "@/types/recruit/offer.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export const offerKeys = createQueryKeys("offer", {
  details: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (id === undefined) throw new ApiPassException("Offer id is required");
      const result = await apiOffers.getById(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  search: (filter: ApiOffersFilter) => ({
    queryKey: [filter],
    queryFn: async ({ signal }) => {
      const result = await apiOffers.search(filter, signal);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return {
        ...result.data,
        items: result.data.items,
      };
    },
  }),
  publicOffer: (uuid: string | undefined, offerId: string | undefined) => ({
    queryKey: ["public-offer", uuid, offerId],
    queryFn: async () => {
      if (!uuid || !offerId) throw new Error("Missing parameters");
      const result = await apiOffers.getPublicOffer(uuid, parseInt(offerId));
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export interface SaveOfferParams {
  id?: number;
  payload: ApiOfferSavePayload;
}

export const useOfferSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined,
  fieldMap: Record<string, string> = {}
) => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({ id, payload }: SaveOfferParams) {
      const result = !id ? await apiOffers.create(payload) : await apiOffers.update(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data.offer_data;
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: offerKeys.search._def });
      void queryClient.invalidateQueries({
        queryKey: offerKeys.details(variables.id).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: candidateActionsKeys.list._def, // TODO how to invalidate only candidate's actions?
      });
    },
    onError: (error: Error) => {
      onError(error, control, fieldMap);
    },
  });
};

export const useOffersSearch = (filter: ApiOffersFilter, enabled = true) => {
  return useQuery({
    ...offerKeys.search(filter),
    enabled: enabled,
  });
};

export const useOfferDetails = (id: number | undefined, enabled: boolean = true) => {
  return useQuery({
    ...offerKeys.details(id),
    enabled,
    staleTime: 60 * 1000,
  });
};

export const useOfferDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiOffers.delete(id);
      if (result.error) throw new ApiErrorException(result.error);
      return id;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: offerKeys.search._def });
      void queryClient.invalidateQueries({
        queryKey: candidateActionsKeys.list._def, // TODO how to invalidate only candidate's actions?
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export interface OfferRequestAnswerPayload {
  id: number;
  approve: boolean;
  payload: ApiOfferApproveRequest;
}

export const useOfferRequestAnswer = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (payload: OfferRequestAnswerPayload) => {
      const { id, approve, payload: approvePayload } = payload;
      const result = approve
        ? await apiOffers.approveRequest(id, approvePayload)
        : await apiOffers.rejectRequest(id, approvePayload);

      if (result.error || !result.data) {
        throw new ApiErrorException(result.error);
      }

      return id;
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({
        queryKey: offerKeys.details(id).queryKey,
      });
      void queryClient.invalidateQueries({ queryKey: offerKeys.search._def });
      void queryClient.invalidateQueries({
        queryKey: candidateActionsKeys.list._def, // TODO how to invalidate only candidate's actions?
      });
      void queryClient.invalidateQueries({ queryKey: employeeTodayKeys.getToday._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useOfferConfirmationCommentUpdate = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: ApiOfferConfirmationCommentUpdate;
    }) => {
      const result = await apiOffers.updateConfirmationComment(id, payload);
      if (result.error) throw new ApiErrorException(result.error);
      return id;
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({ queryKey: offerKeys.details(id).queryKey });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useOfferRequestCancel = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiOffers.cancelRequest(id);
      if (result.error) throw new ApiErrorException(result.error);
      return id;
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({
        queryKey: offerKeys.details(id).queryKey,
      });
      void queryClient.invalidateQueries({ queryKey: offerKeys.search._def });
      void queryClient.invalidateQueries({
        queryKey: candidateActionsKeys.list._def, // TODO how to invalidate only candidate's actions?
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useOfferConfigUpdate = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, config }: { id: number; config: ApiOfferConfig }) => {
      const result = await apiOffers.updateConfig(id, config);
      if (result.error) throw new ApiErrorException(result.error);
      return { id, config };
    },
    onSuccess: ({ id }) => {
      void queryClient.invalidateQueries({
        queryKey: offerKeys.details(id).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const usePublicOffer = (uuid: string | undefined, offerId: string | undefined) => {
  return useQuery({
    ...offerKeys.publicOffer(uuid, offerId),
    enabled: !!uuid && !!offerId,
  });
};
